/**
 * Admin → one affiliate (PRP-MCP-10 work item 1, G1): links, totals, the
 * referred users (signup date, commissionable spend), every commission row
 * (payment, kind, payout state), owed and whether it meets the payout minimum,
 * and "Mark paid": a payout reference stamped on the unpaid rows this page
 * showed (it sends the detail's `as_of`; review L5), refused when nothing is owed.
 * PRP-MCP-11 G5: "Accepted terms v1 at <time>", or "No terms acceptance on file".
 * Admin only on the server: a non-admin gets 403 and sees "Admins only."
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { AdminAffiliatesAPI, affiliateErrText, fmtCents, termsText } from '../../lib/adminAffiliatesApi';
import type { AffiliateDetail } from '../../lib/adminAffiliatesApi';

const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');
const KIND: Record<string, string> = { pack: 'Pack', trial_fee: 'Trial fee', subscription: 'Subscription', refund: 'Refund' };

/** The /admin/affiliates/:id page. */
const AdminAffiliatePage: React.FC = () => {
  const { id = '' } = useParams();
  const [data, setData] = useState<AffiliateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [payoutRef, setPayoutRef] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await AdminAffiliatesAPI.get(id));
    } catch (err) {
      setError(affiliateErrText(err, 'Could not load this affiliate.'));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const markPaid = async () => {
    if (!data) return;
    if (data.owed_cents <= 0) {
      setMsg('Nothing is owed.');
      return;
    }
    if (!payoutRef.trim()) {
      setMsg('Enter the payout reference (PayPal transaction id, check number…).');
      return;
    }
    if (!window.confirm(`Mark ${fmtCents(data.owed_cents)} paid to ${data.affiliate.name} with reference ${payoutRef.trim()}?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await AdminAffiliatesAPI.markPaid(id, payoutRef.trim(), data.as_of);
      setMsg(`Marked ${res.rows} row${res.rows === 1 ? '' : 's'} paid (${fmtCents(res.amount_cents)}), reference ${res.payout_ref}.`);
      setPayoutRef('');
      await load();
    } catch (err) {
      setMsg(affiliateErrText(err, 'Could not mark paid.'));
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{data ? `${data.affiliate.name} (${data.affiliate.code})` : 'Affiliate'}</h1>
      <Link to="/admin/affiliates" className="text-sm text-muted-foreground underline">Affiliates</Link>
    </div>
  );
  if (!data) {
    return (
      <div className="space-y-4">
        {header}
        <p className={error ? 'text-sm font-medium text-red-500' : 'text-sm text-muted-foreground'}>{error || 'Loading…'}</p>
      </div>
    );
  }
  const a = data.affiliate;

  return (
    <div className="space-y-4">
      {header}
      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      <section className="space-y-1 rounded-md border border-border p-4 text-sm" data-testid="affiliate-summary">
        <p>Status: {a.status === 'active' ? 'Active' : 'Paused'} · Payout: {a.payout_method || '—'} to {a.payout_email || '—'}</p>
        <p data-testid="affiliate-terms">{termsText(a.terms_version, a.terms_accepted_at)}</p>
        <p>Links: {a.links.map((l) => <code key={l} className="mr-3 break-all">{l}</code>)}</p>
        <p>
          Commissions {fmtCents(data.commissions_cents)} · Paid out {fmtCents(data.paid_out_cents)} ·{' '}
          <strong>Owed {fmtCents(data.owed_cents)}</strong>{' '}
          {data.meets_minimum
            ? `(meets the ${fmtCents(data.minimum_payout_cents)} minimum)`
            : `(under the ${fmtCents(data.minimum_payout_cents)} minimum)`}
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <input aria-label="Payout reference" placeholder="Payout reference"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} />
          <button type="button" disabled={busy}
            className="rounded-md bg-primary px-3 py-1 text-primary-foreground disabled:opacity-50"
            onClick={() => void markPaid()}>
            Mark paid
          </button>
        </div>
        {msg && <p className="text-muted-foreground">{msg}</p>}
      </section>

      <section className="space-y-2 rounded-md border border-border p-4" data-testid="affiliate-referrals">
        <h2 className="text-base font-semibold">Referrals ({data.referrals.length})</h2>
        {data.referrals.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr><th className="p-1">User</th><th className="p-1">Signed up</th><th className="p-1 text-right">Spend</th><th className="p-1 text-right">Commission</th></tr>
            </thead>
            <tbody>
              {data.referrals.map((r) => (
                <tr key={r.user_id} className="border-t border-border">
                  <td className="p-1"><Link to={`/admin/users/${encodeURIComponent(r.user_id)}`} className="underline">{r.email || r.user_id}</Link></td>
                  <td className="p-1">{fmtDate(r.signed_up_at)}</td>
                  <td className="p-1 text-right">{fmtCents(r.spend_cents)}</td>
                  <td className="p-1 text-right">{fmtCents(r.commission_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="space-y-2 rounded-md border border-border p-4" data-testid="affiliate-commissions">
        <h2 className="text-base font-semibold">Commissions ({data.commissions.length})</h2>
        {data.commissions.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-1">Paid</th><th className="p-1">User</th><th className="p-1">Kind</th>
                <th className="p-1 text-right">Payment</th><th className="p-1 text-right">Commission</th><th className="p-1">Payout</th>
              </tr>
            </thead>
            <tbody>
              {data.commissions.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="p-1">{fmtDate(c.paid_at || c.created_at)}</td>
                  <td className="p-1">{c.referred_email || c.referred_user_id}</td>
                  <td className="p-1">{KIND[c.kind] || c.kind}</td>
                  <td className="p-1 text-right">{fmtCents(c.payment_amount_cents)}</td>
                  <td className="p-1 text-right">{fmtCents(c.commission_cents)}</td>
                  <td className="p-1">{c.paid_out_at ? `Paid ${fmtDate(c.paid_out_at)} (${c.payout_ref})` : 'Unpaid'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default AdminAffiliatePage;
